import { useCallback, useEffect, useState } from "react";
import { CircleCheck, CreditCard, ReceiptText, SearchX, UserRound } from "lucide-react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { formatCurrency, formatDate } from "@/lib/format";
import { ExpenseStateBadge, flagLabel } from "@/components/ExpenseStatus";
import { memberLabel } from "@/components/OrgMemberSelect";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  EmptyState,
  ErrorState,
  HeaderRow,
  LoadingState,
  MerchantCell,
  Pager,
  ReceiptPresence,
  TH,
  TableShell,
  dateRangeQuery,
  prettyCategory,
  prettyMerchant,
  rowLink,
} from "@/components/ExpenseUi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { components } from "@dolfin/api-admin";

type ExpenseResponse = components["schemas"]["ExpenseResponse"];

const PAGE_SIZE = 20;

// The last four is what people recognise a card by, so it shows even when the card has a nickname.
export function cardLabel(expense: {
  cardNetwork?: string | null;
  cardLast4?: string | null;
}) {
  const network = expense.cardNetwork
    ? expense.cardNetwork.charAt(0) + expense.cardNetwork.slice(1).toLowerCase()
    : "Card";
  return expense.cardLast4 ? `${network} •• ${expense.cardLast4}` : network;
}

export interface ExpensesTableProps {
  state?: string;
  /** Only expenses waiting on a person. */
  flagged?: boolean;
  needsReceipt?: boolean;
  cardConnectionId?: string;
  merchantId?: string;
  mine?: boolean;
  /** Only one organisation member's spend; `spenderLabel` names them in the empty state. */
  spenderUserId?: string;
  spenderLabel?: string;
  /** Only spend on or after this day (yyyy-mm-dd). */
  from?: string;
  /** Only spend on or before this day (yyyy-mm-dd). */
  to?: string;
  onSelect?: (expenseId: string) => void;
  /** Bump to force a refetch after the host changes something. */
  reloadSignal?: number;
  onCountChange?: (count: number) => void;
}

export function ExpensesTable({
  state,
  flagged,
  needsReceipt,
  cardConnectionId,
  merchantId,
  mine,
  spenderUserId,
  spenderLabel,
  from,
  to,
  onSelect,
  reloadSignal,
  onCountChange,
}: ExpensesTableProps) {
  const api = useDolfinClient();
  const [expenses, setExpenses] = useState<ExpenseResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const fetchExpenses = useCallback(
    async (cursor?: string) => {
      setLoading(true);
      setError(null);
      const query: Record<string, string | number | boolean> = {
        Limit: PAGE_SIZE,
      };
      if (state) query.State = state;
      if (flagged) query.Flagged = true;
      if (needsReceipt) query.NeedsReceipt = true;
      if (cardConnectionId) query.CardConnectionId = cardConnectionId;
      if (merchantId) query.MerchantId = merchantId;
      if (mine) query.Mine = true;
      if (spenderUserId) query.SpenderUserId = spenderUserId;
      Object.assign(query, dateRangeQuery({ from, to }));
      if (cursor) query.Cursor = cursor;

      const { data, error: apiError } = await api.GET("/v1/expenses", {
        params: { query },
      });
      if (apiError || !data) {
        setError("Failed to load expenses");
      } else {
        const rows = data.data ?? [];
        setExpenses(rows);
        onCountChange?.(rows.length);
        setNextCursor(data.meta?.cursors?.next ?? null);
      }
      setLoading(false);
    },
    [
      api,
      state,
      flagged,
      needsReceipt,
      cardConnectionId,
      merchantId,
      mine,
      spenderUserId,
      from,
      to,
      onCountChange,
    ],
  );

  useEffect(() => {
    setCursorStack([]);
    setNextCursor(null);
    fetchExpenses();
    // reloadSignal is an explicit refetch trigger from the host.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchExpenses, reloadSignal]);

  function handleNextPage() {
    if (!nextCursor) return;
    setCursorStack((prev) => [...prev, nextCursor]);
    fetchExpenses(nextCursor);
  }

  function handlePrevPage() {
    const newStack = [...cursorStack];
    newStack.pop();
    setCursorStack(newStack);
    fetchExpenses(newStack[newStack.length - 1]);
  }

  if (error) {
    return (
      <ErrorState
        message={error}
        onRetry={() => fetchExpenses(cursorStack[cursorStack.length - 1])}
      />
    );
  }

  if (loading) {
    return <LoadingState label="Loading expenses..." />;
  }

  if (expenses.length === 0) {
    if (spenderUserId && !(cardConnectionId || from || to)) {
      return (
        <EmptyState icon={UserRound} title={spenderLabel ? `No spend for ${spenderLabel} here` : "No spend for this person here"}>
          Spend belongs to someone when it is on a card they hold, or an admin has attributed it to them. Switch
          to Everyone to see the whole organisation's.
        </EmptyState>
      );
    }
    if (mine && !(cardConnectionId || from || to)) {
      return (
        <EmptyState icon={UserRound} title="None of your spend here">
          Spend is yours when it is on a card you hold, or an admin has attributed it to you. Switch to
          Everyone to see the whole organisation's.
        </EmptyState>
      );
    }
    const filtered = !!(cardConnectionId || from || to);
    if (filtered) {
      return (
        <EmptyState icon={SearchX} title="Nothing in this view">
          No expenses match the card or dates you picked. Widen the filters to
          see more.
        </EmptyState>
      );
    }
    return flagged ? (
      <EmptyState icon={CircleCheck} title="Nothing is stuck">
        Every expense is coded and moving. Anything waiting for sign-off is
        under To approve.
      </EmptyState>
    ) : state === "PendingReview" ? (
      <EmptyState icon={CircleCheck} title="Nothing waiting for approval">
        Coded spend lands here before it posts to your accounts.
      </EmptyState>
    ) : needsReceipt ? (
      <EmptyState icon={ReceiptText} title="Every expense has a receipt">
        Nothing to chase. New card spend without a receipt will show up here.
      </EmptyState>
    ) : (
      <EmptyState icon={CreditCard} title="No card spend yet">
        Link a card and its transactions appear here as they happen, already
        coded.
      </EmptyState>
    );
  }

  return (
    <>
      <ExpenseRowsTable expenses={expenses} onSelect={onSelect} />

      <Pager
        page={cursorStack.length + 1}
        hasPrevious={cursorStack.length > 0}
        hasNext={!!nextCursor}
        onPrevious={handlePrevPage}
        onNext={handleNextPage}
      />
    </>
  );
}

/** The expense rows on their own: shared by the paged list and search results, which arrive ranked. */
export function ExpenseRowsTable({
  expenses,
  onSelect,
}: {
  expenses: ExpenseResponse[];
  onSelect?: (expenseId: string) => void;
}) {
  return (
    <TableShell>
      <Table>
        <TableHeader>
          <HeaderRow>
            <TableHead className={TH}>Merchant</TableHead>
            <TableHead className={TH}>Spent by</TableHead>
            <TableHead className={TH}>Card</TableHead>
            <TableHead className={TH}>Date</TableHead>
            <TableHead className={TH}>Status</TableHead>
            <TableHead className={TH}>Receipt</TableHead>
            <TableHead className={cn(TH, "text-right")}>Amount</TableHead>
          </HeaderRow>
        </TableHeader>
        <TableBody>
          {expenses.map((expense) => (
            <TableRow
              key={expense.id}
              {...rowLink(onSelect ? () => onSelect(expense.id!) : undefined)}
            >
              <TableCell className="max-w-[300px]">
                <MerchantCell
                  name={expense.merchant?.displayName ?? expense.merchantName}
                  secondary={<MerchantSecondary expense={expense} />}
                  badges={
                    <>
                      {expense.isPersonal ? (
                        <Badge
                          variant="secondary"
                          className="px-1.5 py-0 text-[10px]"
                        >
                          Personal
                        </Badge>
                      ) : null}
                      {/* Coded from the organisation's own past choices rather than a rule: worth a glance. */}
                      {expense.codingSource === "History" ? (
                        <Badge
                          variant="outline"
                          className="border-primary/30 px-1.5 py-0 text-[10px] text-primary"
                          title="Coded from similar expenses you've coded before"
                        >
                          Learned
                        </Badge>
                      ) : null}
                    </>
                  }
                />
              </TableCell>
              <TableCell className="whitespace-nowrap text-sm">
                {expense.spender ? (
                  memberLabel(expense.spender)
                ) : (
                  <span className="text-muted-foreground">Unassigned</span>
                )}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                <div className="text-sm">
                  {expense.cardDisplayName || cardLabel(expense)}
                </div>
                {expense.cardDisplayName ? (
                  <div className="text-xs text-muted-foreground">
                    {cardLabel(expense)}
                  </div>
                ) : null}
              </TableCell>
              <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                {formatDate(expense.transactionDate ?? expense.createdAt)}
              </TableCell>
              <TableCell>
                <ExpenseStateBadge state={expense.state} />
                {expense.flagReason ? (
                  <div className="mt-1 text-xs text-amber-600">
                    {flagLabel(expense.flagReason)}
                  </div>
                ) : null}
              </TableCell>
              <TableCell>
                <ReceiptPresence attached={!expense.needsReceipt} />
              </TableCell>
              <TableCell className="text-right font-semibold whitespace-nowrap tabular-nums">
                {formatCurrency(expense.amount, expense.currency)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableShell>
  );
}

// The category, and the card's own spelling when it differs from the merchant's name, so "Blue Anchor" still
// reads as the "ZETTLE_*BLUEANC" on the statement.
function MerchantSecondary({ expense }: { expense: ExpenseResponse }) {
  const category =
    prettyCategory(expense.spendCategoryLabel) ?? "Uncategorised";
  const raw = expense.merchantName;
  const differs =
    expense.merchant?.displayName &&
    raw &&
    prettyMerchant(raw) !== expense.merchant.displayName;
  return (
    <>
      {differs ? <span className="font-mono text-[11px]">{raw}</span> : null}
      {differs ? " · " : null}
      {category}
    </>
  );
}
